import { Component, Input, OnInit, OnDestroy } from '@angular/core';
import { Unsubscriber } from '@core-lib/classes';
import { ModalRef } from '@core-lib/modules/modal-container';
import { combineLatest, map, of, takeUntil } from 'rxjs';
import { FormBuilder, FormControl, FormGroup } from '@angular/forms';
import { Observable } from 'rxjs';
import { ToastrService } from 'ngx-toastr';
import { TranslateService } from '@ngx-translate/core';
import { PreferenceService } from '@core-lib/services/preference.service';
import { IEditPreferences, IPreference, IPreferenceModeReceptionForm, PreferenceModeReception } from '@core-lib/models/preference.model';
import { Preference } from '@core-lib/enums/preference.enum';
import { IModeReception } from '@core-lib/models/mode-reception.model';

@Component({
    selector: 'settings-mode-reception',
    templateUrl: './settings-mode-reception.component.html',
    styleUrls: ['./settings-mode-reception.component.scss'],
    standalone: false
})
export class SettingsModeReceptionComponent extends Unsubscriber implements OnInit, OnDestroy {
	@Input() structureId: string;
	@Input() allowEdit: boolean;
	@Input() modesReception$: Observable<IModeReception[]> = of([]);
	@Input() preferenceModeReception$: Observable<PreferenceModeReception> = of(null);

	modalRef: ModalRef;
	form: FormGroup<IPreferenceModeReceptionForm>;

	modesReception: IModeReception[] = [];
	preferenceModeReception: PreferenceModeReception | null = null;
	modesReceptionPreference: IModeReception | null;
	anyEditMode$: Observable<boolean> = this.preferenceService.anyEditMode$;

	get isEditMode() {
		return this.preferenceService.modeReceptionEditMode$.value;
	}

	constructor(
		private fb: FormBuilder,
		public preferenceService: PreferenceService,
		private toastr: ToastrService,
		private translate: TranslateService
	) {
		super();
	}

	ngOnInit(): void {
		this.preferenceModeReception$?.pipe(takeUntil(this.destroySubscriptions$)).subscribe(pref => {
			if (pref) {
				this.preferenceModeReception = pref;
				this.modesReceptionPreference = this.modesReception.find(m => m.id === pref.retour.valeur);
				this.initForm();
			}
		});
		
		this.modesReception$?.pipe(takeUntil(this.destroySubscriptions$)).subscribe(modesReception => {
			this.modesReception = modesReception ?? [];
			this.modesReceptionPreference = this.modesReception.find(m => m.id === this.preferenceModeReception.retour.valeur);
			this.initForm();
		});
	}

	private initForm(): void {
		this.preferenceService.modeReceptionEditMode$.next(false);
		this.form = this.fb.group<IPreferenceModeReceptionForm>({
			retour: new FormControl(this.modesReceptionPreference?.id ?? null)
		});

		this.modesReception$.pipe(takeUntil(this.destroySubscriptions$)).subscribe(modes => {
			const current = this.form.controls.retour.value;
			const exists = modes.some(m => m.id === current);
			if (!exists) {
				this.form.controls.retour.setValue(null, { emitEvent: false });
			}
		});
	}

	edit(): void {
		this.preferenceService.modeReceptionEditMode$.next(true);
	}

	cancel(): void {
		this.initForm();
	}

	confirm(): void {
		if (!this.allowEdit) return;

		if (this.form.invalid) {
			this.form.markAllAsTouched();
			return;
		}

		const preferences: IPreference[] = [
			{
				actif: true,
				code: Preference.modeReception.retour.code,
				valeur: this.form.value.retour,
				utilisateurModification: null,
				dateModification: null
			}
		];

		const editPreferences: IEditPreferences = {
			category: Preference.category.modeReception,
			preferences: preferences
		};

		this.preferenceService.edit(this.structureId, editPreferences).subscribe({
			next: () => {
				this.translate.get('Success.Save').subscribe(msg => this.toastr.success(msg));
				this.preferenceService.modeReceptionEditMode$.next(false);
				this.preferenceService.get(this.structureId, true);
			},
			error: error => {
				this.toastr.error(error);
			}
		});
	}
}
